import React, { useState, useEffect } from 'react';
import { FaTimes, FaTrash, FaChevronRight, FaChevronDown, FaSearch, FaCheck, FaUndo } from 'react-icons/fa';

const IssueDetailsPanel = ({
    issue,
    members,
    tasks,
    isAdmin,
    currentUserEmail,
    onClose,
    onSave,
    onDelete,
    onDecide
}) => {
    const isNewIssue = !issue;
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        status: 'New',
        priority: '',
        type: '',
        assignedToEmail: '',
        assignedToName: '',
        blockedTasks: []
    });
    const [saving, setSaving] = useState(false);
    const [expandedTasks, setExpandedTasks] = useState(new Set());
    const [blockerSearch, setBlockerSearch] = useState('');
    const [clientNote, setClientNote] = useState('');
    // Admin decision: which button was pressed, and the comment it requires
    const [pendingDecision, setPendingDecision] = useState(null); // 'approve' | 'reject'
    const [decisionComment, setDecisionComment] = useState('');

    // Only the assignee may act on their own issue. Every other client user -
    // including client admins - stays read-only.
    //
    // A Closed issue is locked to the client: the Migration Experts own
    // closure, so the assignee cannot reopen or comment on what an admin has
    // closed. The backend refuses it too - this is not the only guard.
    const canClientEdit = !isAdmin && !!issue && !!currentUserEmail
        && issue.assignedToEmail === currentUserEmail
        && issue.status !== 'Closed';
    const canEditStatus = isAdmin || canClientEdit;

    useEffect(() => {
        if (issue) {
            setFormData({
                title: issue.title || '',
                description: issue.description || '',
                status: issue.status || '',
                priority: issue.priority || '',
                type: issue.type || '',
                assignedToEmail: issue.assignedToEmail || '',
                assignedToName: issue.assignedToName || '',
                blockedTasks: issue.blockedTasks || []
            });

            // Auto-expand main tasks that have blocked subtasks
            if (issue.blockedTasks?.length > 0 && tasks?.length > 0) {
                const subtaskIds = new Set(issue.blockedTasks);
                const mainTasksToExpand = new Set();
                tasks.filter(t => t.mainTaskId).forEach(subtask => {
                    if (subtaskIds.has(subtask.taskId?.toString())) {
                        mainTasksToExpand.add(subtask.mainTaskId);
                    }
                });
                setExpandedTasks(mainTasksToExpand);
            }
        }
    }, [issue, tasks]);

    // Clear the draft note only when a different issue is opened - the effect
    // above also runs on a tasks refresh, which must not wipe typed text
    useEffect(() => {
        setClientNote('');
        setPendingDecision(null);
        setDecisionComment('');
    }, [issue?.issueId]);

    // Check if form has unsaved changes
    const hasChanges = issue ? (
        formData.title !== (issue.title || '') ||
        formData.description !== (issue.description || '') ||
        formData.status !== (issue.status || '') ||
        formData.priority !== (issue.priority || '') ||
        formData.type !== (issue.type || '') ||
        formData.assignedToEmail !== (issue.assignedToEmail || '') ||
        JSON.stringify(formData.blockedTasks.sort()) !== JSON.stringify((issue.blockedTasks || []).sort())
    ) : true;

    const handleBlockedTaskToggle = (taskId) => {
        setFormData(prev => {
            const current = prev.blockedTasks || [];
            const taskIdStr = taskId.toString();
            if (current.includes(taskIdStr)) {
                return { ...prev, blockedTasks: current.filter(id => id !== taskIdStr) };
            } else {
                return { ...prev, blockedTasks: [...current, taskIdStr] };
            }
        });
    };

    // Build hierarchical task list: main tasks with their subtasks
    const mainTasks = (tasks || []).filter(t => !t.mainTaskId);
    const subtasks = (tasks || []).filter(t => t.mainTaskId);
    const taskHierarchy = mainTasks.map(mainTask => ({
        ...mainTask,
        subtasks: subtasks.filter(st => st.mainTaskId === mainTask.taskId)
    }));

    // Filter out main tasks with no subtasks, then apply search
    const tasksWithSubtasks = taskHierarchy.filter(mainTask => mainTask.subtasks.length > 0);
    const filteredTaskHierarchy = blockerSearch.trim()
        ? tasksWithSubtasks.map(mainTask => ({
            ...mainTask,
            subtasks: mainTask.subtasks.filter(st =>
                st.subject.toLowerCase().includes(blockerSearch.toLowerCase())
            )
        })).filter(mainTask =>
            mainTask.subject.toLowerCase().includes(blockerSearch.toLowerCase()) ||
            mainTask.subtasks.length > 0
        )
        : tasksWithSubtasks;

    const toggleTaskExpanded = (taskId) => {
        setExpandedTasks(prev => {
            const newSet = new Set(prev);
            if (newSet.has(taskId)) {
                newSet.delete(taskId);
            } else {
                newSet.add(taskId);
            }
            return newSet;
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAssigneeChange = (e) => {
        const email = e.target.value;
        if (!email) {
            setFormData(prev => ({
                ...prev,
                assignedToEmail: '',
                assignedToName: ''
            }));
        } else {
            const member = members.find(m => m.email === email);
            if (member) {
                setFormData(prev => ({
                    ...prev,
                    assignedToEmail: member.email,
                    assignedToName: member.name
                }));
            }
        }
    };

    // Approve or reject a client's resolution. The comment is mandatory on
    // both - a decision with no explanation helps nobody.
    const handleDecision = async () => {
        const comment = decisionComment.trim();
        if (comment.length < 10) {
            alert('Please add a comment of at least 10 characters explaining your decision');
            return;
        }
        setSaving(true);
        try {
            await onDecide(pendingDecision, comment);
            setPendingDecision(null);
            setDecisionComment('');
        } catch (err) {
            // onDecide has surfaced the failure; keep the typed comment
        } finally {
            setSaving(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // The assignee only ever submits a status change plus a note. Values
        // computed inline rather than read from consts declared further down.
        if (canClientEdit) {
            const note = clientNote.trim();
            if (formData.status === 'Resolved by Client' && note.length < 10) {
                alert('Please add a note of at least 10 characters explaining how this was resolved');
                return;
            }
            setSaving(true);
            try {
                await onSave({ status: formData.status, note });
                // Saved: the note is part of the trail now, so empty the box.
                // Without this, clientHasChanges stays true and the button
                // never settles back to 'Close'.
                setClientNote('');
            } catch (err) {
                // onSave has already surfaced the failure. Keep the typed note
                // so a failed save does not lose what they wrote.
            } finally {
                setSaving(false);
            }
            return;
        }

        if (!formData.title.trim()) {
            alert('Title is required');
            return;
        }
        if (!formData.priority) {
            alert('Priority is required');
            return;
        }
        if (!formData.type) {
            alert('Type is required');
            return;
        }
        setSaving(true);
        try {
            await onSave(formData);
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Members are DynamoDB clients (from project-clients endpoint)
    const assignableMembers = members.filter(m => m.email);

    const statusOptions = ['New', 'Acknowledged', 'Need Info', 'Resolved by Client', 'Closed'];
    // The assignee cannot reopen with 'New' or close - they mark it
    // 'Resolved by Client' and an admin confirms with 'Closed'
    const clientStatusOptions = ['Acknowledged', 'Need Info', 'Resolved by Client'];
    const typeOptions = ['', 'Functionality', 'Migration', 'Project'];

    // A client looking at an issue still on 'New' (or already 'Closed') must
    // see its real status, so show it as a disabled option alongside theirs
    const statusChoices = isAdmin
        ? statusOptions.map(value => ({ value, disabled: false }))
        : [
            ...(clientStatusOptions.includes(issue?.status) || !issue?.status
                ? []
                : [{ value: issue.status, disabled: true }]),
            ...clientStatusOptions.map(value => ({ value, disabled: false }))
        ];

    const noteRequired = canClientEdit && formData.status === 'Resolved by Client';
    const noteTooShort = noteRequired && clientNote.trim().length < 10;
    const clientHasChanges = canClientEdit
        && (formData.status !== (issue?.status || '') || clientNote.trim().length > 0);

    const notes = issue?.notes || [];

    // An admin decides only on what the client has actually resolved
    const canDecide = isAdmin && !isNewIssue && issue?.status === 'Resolved by Client';
    const decisionTooShort = decisionComment.trim().length < 10;

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center p-4 z-[60]" onClick={onClose}>
            <div className={`bg-white rounded-lg shadow-xl max-w-4xl w-full overflow-hidden flex flex-col ${isAdmin ? 'h-[65vh]' : 'max-h-[95vh]'}`} onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="sticky top-0 bg-white border-b p-6 z-10">
                    <div className="flex justify-between items-start">
                        <div className="flex-1">
                            {/* Issue Badge */}
                            <div className="mb-3">
                                <span className="inline-flex items-center bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-xs font-medium">
                                    {isNewIssue ? 'New Issue' : issue.issueId}
                                </span>
                            </div>

                            {/* Title */}
                            {isAdmin ? (
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    placeholder="Enter issue title *"
                                    className="text-2xl font-semibold text-gray-900 w-full border-b-2 border-gray-300 focus:border-primary-600 focus:outline-none pb-1"
                                    required
                                />
                            ) : (
                                <h2 className="text-2xl font-semibold text-gray-900">
                                    {issue?.title}
                                </h2>
                            )}

                            {/* Status and Priority Row */}
                            <div className="flex items-center gap-3 mt-3 flex-wrap">
                                {canEditStatus ? (
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                                    >
                                        {statusChoices.map(opt => (
                                            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                                                {opt.value}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    <span className={`px-3 py-1 rounded text-sm font-medium ${
                                        issue?.status === 'Closed' ? 'bg-green-100 text-green-700' :
                                        issue?.status === 'Resolved by Client' ? 'bg-teal-100 text-teal-700' :
                                        issue?.status === 'Need Info' ? 'bg-red-100 text-red-700' :
                                        issue?.status === 'Acknowledged' ? 'bg-amber-100 text-amber-700' :
                                        'bg-blue-100 text-blue-700'
                                    }`}>
                                        {issue?.status}
                                    </span>
                                )}

                                {isAdmin ? (
                                    <select
                                        name="priority"
                                        value={formData.priority}
                                        onChange={handleChange}
                                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                                    >
                                        <option value="">Priority *</option>
                                        <option value="High">Priority: High</option>
                                        <option value="Medium">Priority: Medium</option>
                                        <option value="Low">Priority: Low</option>
                                    </select>
                                ) : issue?.priority && (
                                    <span className={`px-3 py-1 rounded text-sm font-medium ${
                                        issue.priority === 'High' ? 'bg-red-100 text-red-700' :
                                        issue.priority === 'Medium' ? 'bg-orange-100 text-orange-700' :
                                        'bg-blue-100 text-blue-700'
                                    }`}>
                                        {issue.priority} Priority
                                    </span>
                                )}

                                {isAdmin ? (
                                    <select
                                        name="type"
                                        value={formData.type}
                                        onChange={handleChange}
                                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                                    >
                                        <option value="">Type *</option>
                                        {typeOptions.filter(t => t).map(opt => (
                                            <option key={opt} value={opt}>Type: {opt}</option>
                                        ))}
                                    </select>
                                ) : issue?.type && (
                                    <span className="px-3 py-1 rounded text-sm font-medium bg-gray-100 text-gray-700">
                                        Type: {issue.type}
                                    </span>
                                )}
                            </div>
                        </div>

                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors ml-4"
                        >
                            <FaTimes className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="space-y-6">
                        {/* Description */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Description
                            </label>
                            {isAdmin ? (
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows={4}
                                    placeholder="Describe the issue..."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                                />
                            ) : (
                                <div className="bg-gray-50 rounded-lg p-4">
                                    <p className="text-gray-700 whitespace-pre-wrap">{issue?.description || 'No description'}</p>
                                </div>
                            )}
                        </div>

                        {/* Admin decision on a client's resolution */}
                        {canDecide && (
                            <div className="border border-amber-300 bg-amber-50 rounded-lg p-4">
                                <p className="text-sm font-semibold text-amber-900 mb-1">
                                    The client has marked this resolved
                                </p>
                                <p className="text-xs text-amber-800 mb-3">
                                    Approve to close it, or reject to send it back with what still needs doing.
                                    Either way the client is emailed your comment.
                                </p>

                                {!pendingDecision ? (
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setPendingDecision('approve')}
                                            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
                                        >
                                            <FaCheck size={12} />
                                            Approve &amp; close
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setPendingDecision('reject')}
                                            className="flex items-center gap-2 px-4 py-2 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 text-sm font-medium"
                                        >
                                            <FaUndo size={12} />
                                            Reject
                                        </button>
                                    </div>
                                ) : (
                                    <div>
                                        <textarea
                                            value={decisionComment}
                                            onChange={(e) => setDecisionComment(e.target.value)}
                                            rows={3}
                                            autoFocus
                                            placeholder={pendingDecision === 'approve'
                                                ? 'Confirm what you verified (required, at least 10 characters) *'
                                                : 'Explain what still needs doing (required, at least 10 characters) *'}
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 resize-none text-sm ${
                                                decisionTooShort && decisionComment.length > 0
                                                    ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
                                                    : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                                            }`}
                                        />
                                        <div className="flex items-center gap-2 mt-2">
                                            <button
                                                type="button"
                                                onClick={handleDecision}
                                                disabled={saving || decisionTooShort}
                                                className={`px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ${
                                                    pendingDecision === 'approve'
                                                        ? 'bg-green-600 hover:bg-green-700'
                                                        : 'bg-red-600 hover:bg-red-700'
                                                }`}
                                            >
                                                {saving && (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                                )}
                                                {pendingDecision === 'approve' ? 'Confirm approval' : 'Confirm rejection'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setPendingDecision(null);
                                                    setDecisionComment('');
                                                }}
                                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 text-sm font-medium"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* The conversation - both sides, newest first */}
                        {(notes.length > 0 || canClientEdit) && (
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Discussion {notes.length > 0 && `(${notes.length})`}
                                </label>

                                {notes.length > 0 && (
                                    <ul className="space-y-2 mb-3">
                                        {[...notes].reverse().map((note, idx) => {
                                            const fromAdmin = note.authorType === 'admin';
                                            // An admin entry is a decision, so label it by the
                                            // decision rather than the raw status it produced
                                            const label = fromAdmin
                                                ? (note.status === 'Closed' ? 'Approved' : 'Rejected')
                                                : `set ${note.status}`;
                                            return (
                                                <li
                                                    key={`${note.at}-${idx}`}
                                                    className={`rounded-lg p-3 border-l-4 ${
                                                        fromAdmin
                                                            ? 'bg-indigo-50 border-indigo-400'
                                                            : 'bg-gray-50 border-gray-300'
                                                    }`}
                                                >
                                                    <div className="flex items-center justify-between gap-2 mb-1">
                                                        <span className="text-xs font-medium text-gray-700">
                                                            {note.authorName}
                                                            {fromAdmin && (
                                                                <span className="ml-2 px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 text-[10px] font-semibold uppercase">
                                                                    Migration Experts
                                                                </span>
                                                            )}
                                                        </span>
                                                        <span className="text-xs text-gray-500">
                                                            {label} &middot; {formatDate(note.at)}
                                                        </span>
                                                    </div>
                                                    {note.text ? (
                                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.text}</p>
                                                    ) : (
                                                        <p className="text-sm text-gray-400 italic">No note</p>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}

                                {canClientEdit && (
                                    <>
                                        <textarea
                                            value={clientNote}
                                            onChange={(e) => setClientNote(e.target.value)}
                                            rows={3}
                                            placeholder={noteRequired
                                                ? 'Explain how this was resolved (required, at least 10 characters) *'
                                                : 'Add a note (optional)'}
                                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 resize-none ${
                                                noteTooShort && clientNote.length > 0
                                                    ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
                                                    : 'border-gray-300 focus:ring-primary-500 focus:border-primary-500'
                                            }`}
                                        />
                                        {noteRequired && (
                                            <p className={`text-xs mt-1 ${noteTooShort ? 'text-red-600' : 'text-gray-500'}`}>
                                                A note of at least 10 characters is required to mark this resolved.
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Assigned To */}
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Assigned To
                            </label>
                            {isAdmin ? (
                                <select
                                    value={formData.assignedToEmail || ''}
                                    onChange={handleAssigneeChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                >
                                    <option value="">Select team member...</option>
                                    {assignableMembers.some(m => m.group === 'admin') && (
                                        <optgroup label="Admin Users">
                                            {assignableMembers.filter(m => m.group === 'admin').map(member => (
                                                <option key={member.email} value={member.email} disabled={member.disabled === true}>
                                                    {member.name} ({member.email}){member.disabled ? ' - disabled' : ''}
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                    {assignableMembers.some(m => m.group === 'client') && (
                                        <optgroup label="Client Users">
                                            {assignableMembers.filter(m => m.group === 'client').map(member => (
                                                <option key={member.email} value={member.email} disabled={member.disabled === true}>
                                                    {member.name} ({member.email}){member.disabled ? ' - disabled' : ''}
                                                </option>
                                            ))}
                                        </optgroup>
                                    )}
                                    {assignableMembers.some(m => !m.group) && assignableMembers.filter(m => !m.group).map(member => (
                                        <option key={member.email} value={member.email} disabled={member.disabled === true}>
                                            {member.name} ({member.email}){member.disabled ? ' - disabled' : ''}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                <p className="text-gray-700">{issue?.assignedToName || 'Not assigned'}</p>
                            )}
                        </div>

                        {/* Blocked Tasks */}
                        <div className={isAdmin ? "flex-1 flex flex-col min-h-0" : ""}>
                            <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Blocked Tasks {formData.blockedTasks.length > 0 && `(${formData.blockedTasks.length})`}
                            </label>
                            {isAdmin ? (
                                <div className="border border-gray-300 rounded-lg flex-1 flex flex-col min-h-0">
                                    {/* Search */}
                                    <div className="p-2 border-b border-gray-200">
                                        <div className="relative">
                                            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={12} />
                                            <input
                                                type="text"
                                                value={blockerSearch}
                                                onChange={(e) => setBlockerSearch(e.target.value)}
                                                placeholder="Search tasks..."
                                                className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                            />
                                        </div>
                                    </div>
                                    <div className="flex-1 overflow-y-auto">
                                        {filteredTaskHierarchy.length === 0 ? (
                                            <p className="p-3 text-gray-500 text-sm">No tasks found</p>
                                        ) : (
                                            filteredTaskHierarchy.map(mainTask => (
                                                <div key={mainTask.taskId} className="border-b border-gray-100 last:border-b-0">
                                                    {/* Main Task */}
                                                    <div className="flex items-center gap-1 px-2 py-2 hover:bg-gray-50">
                                                        {mainTask.subtasks.length > 0 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleTaskExpanded(mainTask.taskId)}
                                                                className="p-1 text-gray-400 hover:text-gray-600"
                                                            >
                                                                {expandedTasks.has(mainTask.taskId) ? (
                                                                    <FaChevronDown size={10} />
                                                                ) : (
                                                                    <FaChevronRight size={10} />
                                                                )}
                                                            </button>
                                                        )}
                                                        {mainTask.subtasks.length === 0 && <span className="w-5" />}
                                                        <label className="flex items-center gap-2 flex-1 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={formData.blockedTasks.includes(mainTask.taskId?.toString())}
                                                                onChange={() => handleBlockedTaskToggle(mainTask.taskId)}
                                                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                            />
                                                            <span className="text-sm font-medium text-gray-900">{mainTask.subject}</span>
                                                        </label>
                                                    </div>
                                                    {/* Subtasks - only show if expanded OR if search matches subtask */}
                                                    {(expandedTasks.has(mainTask.taskId) || blockerSearch.trim()) && mainTask.subtasks.map(subtask => (
                                                        <label key={subtask.taskId} className="flex items-center gap-2 px-3 py-2 pl-10 hover:bg-gray-50 cursor-pointer bg-gray-50">
                                                            <input
                                                                type="checkbox"
                                                                checked={formData.blockedTasks.includes(subtask.taskId?.toString())}
                                                                onChange={() => handleBlockedTaskToggle(subtask.taskId)}
                                                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                                            />
                                                            <span className={`text-sm ${subtask.isClosed ? 'text-gray-400 line-through' : 'text-gray-700'}`}>{subtask.subject}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-gray-50 rounded-lg p-3">
                                    {(issue?.blockedTasks || []).length === 0 ? (
                                        <p className="text-gray-500 text-sm">No blocked tasks</p>
                                    ) : (
                                        <ul className="text-sm text-gray-700 space-y-1">
                                            {(issue?.blockedTasks || []).map(taskId => {
                                                const task = tasks.find(t => t.taskId?.toString() === taskId);
                                                if (!task) return null;
                                                // Find parent main task if this is a subtask
                                                const parentTask = task.mainTaskId
                                                    ? tasks.find(t => t.taskId === task.mainTaskId)
                                                    : null;
                                                const closedStyle = task.isClosed ? 'text-gray-400 line-through' : '';
                                                return (
                                                    <li key={taskId} className={`flex items-center gap-2 ${closedStyle}`}>
                                                        <span className="w-1.5 h-1.5 bg-gray-400 rounded-full flex-shrink-0"></span>
                                                        {parentTask ? (
                                                            <span>
                                                                <span className={task.isClosed ? '' : 'text-gray-500'}>{parentTask.subject}</span>
                                                                <span className={task.isClosed ? '' : 'text-gray-400'} style={{margin: '0 0.25rem'}}>/</span>
                                                                {task.subject}
                                                            </span>
                                                        ) : (
                                                            task.subject
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Metadata */}
                        {issue && (
                            <div className="text-xs text-gray-500 pt-4 border-t border-gray-200">
                                <p>Created by {issue.createdByName} on {formatDate(issue.createdAt)}</p>
                                {issue.updatedAt !== issue.createdAt && (
                                    <p>Last updated: {formatDate(issue.updatedAt)}</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
                    {isAdmin && issue && (
                        <button
                            type="button"
                            onClick={() => onDelete(issue.issueId)}
                            className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg font-medium transition-colors"
                        >
                            <FaTrash size={14} />
                            Delete
                        </button>
                    )}
                    <div className="flex-1" />
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 font-medium transition-colors"
                        >
                            {isAdmin || canClientEdit ? 'Cancel' : 'Close'}
                        </button>
                        {isAdmin && (
                            <button
                                type="button"
                                onClick={hasChanges ? handleSubmit : onClose}
                                disabled={saving}
                                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {saving && (
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                )}
                                {isNewIssue ? 'Create Issue' : (hasChanges ? 'Save Changes' : 'Close')}
                            </button>
                        )}
                        {canClientEdit && (
                            <button
                                type="button"
                                onClick={clientHasChanges ? handleSubmit : onClose}
                                disabled={saving || (clientHasChanges && noteTooShort)}
                                className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {saving && (
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                )}
                                {clientHasChanges ? 'Save Changes' : 'Close'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IssueDetailsPanel;
